import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useState } from "react";

/**
 * Interface pour représenter une position géographique
 */
export interface GeoPosition {
  latitude: number;
  longitude: number;
}

/**
 * Options pour la requête de géolocalisation
 */
export interface GeolocationOptions {
  enableHighAccuracy?: boolean;
  timeout?: number;
  maximumAge?: number;
}

/**
 * État de la géolocalisation
 */
export interface GeolocationState {
  position: GeoPosition | null;
  error: Error | null;
  loading: boolean;
}

/**
 * Demande la permission de géolocalisation et récupère la position actuelle de l'utilisateur
 * @param options Options pour la requête de géolocalisation
 * @returns Une promesse qui se résout avec la position de l'utilisateur ou rejette avec une erreur
 */
export const getUserLocation = (options: GeolocationOptions = {}): Promise<GeoPosition> => {
  const defaultOptions: GeolocationOptions = {
    enableHighAccuracy: true,
    timeout: 5000,
    maximumAge: 0,
    ...options
  };
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject();
      return;
    }
    navigator.geolocation.getCurrentPosition(position => {
      resolve({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    }, err => {
      reject(new Error(err.message));
    }, defaultOptions as PositionOptions);
  });
};

/**
 * Hook React pour utiliser la géolocalisation
 * @param options Options pour la requête de géolocalisation
 * @param skipOnMount Si vrai, ne demande pas automatiquement la géolocalisation au montage du composant
 * @returns Un objet contenant la position, l'erreur, si le chargement est en cours, et une fonction pour demander la géolocalisation
 */
export const useGeolocation = (t0, t1) => {
  const $ = _c(15);
  let t2;
  if ($[0] !== t0) {
    t2 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const options = t2;
  const skipOnMount = t1 === undefined ? false : t1;
  const t3 = !skipOnMount;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      position: null,
      error: null,
      loading: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const [state, setState] = useState(t4);
  let t5;
  if ($[4] !== options) {
    t5 = async () => {
      setState(_temp);
      ;
      try {
        const position = await getUserLocation(options);
        setState({
          position,
          error: null,
          loading: false
        });
      } catch (t6) {
        const error = t6;
        setState({
          position: null,
          error: error as Error,
          loading: false
        });
      }
    };
    $[4] = options;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const getPosition = t5;
  let t6;
  if ($[6] !== getPosition || $[7] !== skipOnMount) {
    t6 = () => {
      if (!skipOnMount) {
        getPosition();
      }
    };
    $[6] = getPosition;
    $[7] = skipOnMount;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const getPositionOnMount = useEffectEvent(t6);
  let t7;
  if ($[9] !== getPositionOnMount) {
    t7 = () => {
      getPositionOnMount();
    };
    $[9] = getPositionOnMount;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = [];
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  useEffect(t7, t8);
  let t9;
  if ($[12] !== getPosition || $[13] !== state) {
    t9 = {
      ...state,
      getPosition
    };
    $[12] = getPosition;
    $[13] = state;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  return t9;
};
function _temp(prev) {
  return {
    ...prev,
    loading: true,
    error: null
  };
}